import { IconButton } from '@zendeskgarden/react-buttons'
import EmojiPicker, { IEmojiData } from 'emoji-picker-react'
import moment from 'moment'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useRevalidator } from 'react-router'
import styled from 'styled-components'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { useOnClickOutside } from '../../hooks/useOnClickOutside'
import MessageAttachmentIcon from '../../icons/message_input_attachment.svg?react'
import MessageCameraIcon from '../../icons/message_input_camera.svg?react'
import MessageEmojiIcon from '../../icons/message_input_emoji.svg?react'
import { BaseRecord } from '../../models/BaseRecord'
import { Campaign } from '../../models/Campaign'
import { Message } from '../../models/Message'
import { RecordFactory } from '../../models/RecordFactory'
import { Team } from '../../models/Team'
import { Template } from '../../models/Template'
import { User } from '../../models/User'
import { PSApplication } from '../app/PSApplication'
import { usePhotoUpload } from '../settings/hooks'
import { Attachment, AttachmentType, Link, MessageAttachmentModal } from './MessageAttachmentModal'
import { TemplateModal } from './TemplateModal'

const BarWrapper = styled.div`
    z-index: 150;
    margin: 0;
    padding: 0 8px;
    display: flex;
    flex-direction: row;
    overflow: hidden;
    border-top: 1px solid #e4e4e4;
    background: #ffffff;
    align-items: center;
`

const InputField = styled.div`
    flex: 1 1 auto;
    background-color: #ffffff;
    border: none;
    margin: 8px 0;
    min-width: 100px;
    padding: 6px 8px 6px 12px;
    cursor: text;
`

interface MessageInputBarProps {
    currentThread: BaseRecord
    searchText: string
    inputBarHeight: number
    onUpdateInputBarHeight: (height: number) => void
}

export const MessageInputBar: React.FC<MessageInputBarProps> = ({
    currentThread,
    searchText,
    inputBarHeight,
    onUpdateInputBarHeight,
}) => {
    const revalidator = useRevalidator()

    const { logAndDisplayError } = useErrorHandler()

    const [showSavedMessagesModal, setShowSavedMessagesModal] = useState(false)
    const [attachmentType, setAttachmentType] = useState<AttachmentType>()
    const [inputSelectionStart, setInputSelectionStart] = useState(0)
    const [inputSelectionEnd, setInputSelectionEnd] = useState(0)
    const [templateID, setTemplateID] = useState('')

    const [templatesEnabled, setTemplatesEnabled] = useState(false)
    const [widgetEnabled, setWidgetEnabled] = useState(false)
    const [formsEnabled, setFormsEnabled] = useState(false)
    const [contractsEnabled, setContractsEnabled] = useState(false)

    const owner = PSApplication.owner()
    const messageRef = useRef<HTMLTextAreaElement>(null)

    const [showAttachmentMenu, setShowAttachmentMenu] = useState(false)
    const attachmentMenuContainerRef = useRef<HTMLDivElement>(null)
    useOnClickOutside(attachmentMenuContainerRef, () => {
        setShowAttachmentMenu(false)
    })

    const [showEmojiPicker, setShowEmojiPicker] = useState(false)
    const emojiMenuContainerRef = useRef<HTMLDivElement>(null)
    useOnClickOutside(emojiMenuContainerRef, () => {
        setShowEmojiPicker(false)
    })

    useEffect(() => {
        if (!owner) return

        setTemplatesEnabled(owner.allowTemplate || false)
        setWidgetEnabled(owner.allowWidget || false)
        setFormsEnabled((!(currentThread instanceof Team) && owner.allowRecord) || false)
        setContractsEnabled((!(currentThread instanceof Team) && owner.allowContract) || false)
    }, [currentThread, owner])

    const sendMessageOrBroadcast = async (message: string, image?: File) => {
        const newMessage = new Message()
        newMessage.message = message

        let isBroadcast = false
        let team: Team | undefined

        if (currentThread instanceof Team) {
            team = currentThread
            newMessage.team = team
            isBroadcast = !!team.id && !!team.bcc
        }
        else if (currentThread instanceof User) {
            newMessage.recipient = currentThread
        }

        if (templateID) {
            newMessage.template = new Template({ id: templateID })
        }

        resetMessageInput()

        if (isBroadcast && team) {
            const result = await RecordFactory.getInstance().sendBroadcast(newMessage, team, image)
            if (!result.ok) {
                logAndDisplayError(result.error)
            }
        }
        else {
            const result = await RecordFactory.getInstance().sendMessage(newMessage, image)
            if (!result.ok) {
                logAndDisplayError(result.error)
            }
        }

        revalidator.revalidate()
    }

    const [addPhotoRef, selectPhoto, selectedPhoto] = usePhotoUpload({
        disabled: false,
        setPhoto: (image: File) => sendMessageOrBroadcast('', image),
    })

    const handleSelectedAttachment = (attachment: Attachment) => {
        if (attachmentType === 'link' && messageRef.current) {
            messageRef.current.value = (attachment as Link).message
            onKeyUp()
            messageRef.current.focus()
        }
        setAttachmentType(undefined)
    }

    // Focus on the send message input box when this component loads
    useEffect(() => {
        if (messageRef.current && !searchText) {
            messageRef.current.focus()
        }
    }, [messageRef, searchText])

    const resetMessageInput = () => {
        if (messageRef.current) {
            messageRef.current.value = ''
        }
        if (templateID) {
            setTemplateID('')
        }
    }

    const onKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()

            const msg = messageRef.current?.value.trim()
            if (!msg) return

            sendMessageOrBroadcast(msg, undefined)
        }
    }

    const onKeyUp = () => {
        if (templateID && messageRef.current?.value === '') {
            setTemplateID('')
        }

        if (messageRef.current) {
            const height = Math.min(messageRef.current.scrollHeight, 160)
            messageRef.current.style.height = `${height}px`
            if (inputBarHeight !== height) {
                onUpdateInputBarHeight(height)
            }
        }
    }

    const onBlur = () => {
        if (messageRef.current) {
            setInputSelectionStart(messageRef.current.selectionStart)
            setInputSelectionEnd(messageRef.current.selectionEnd)
        }
    }

    const onEmojiClick = (event: React.MouseEvent, data: IEmojiData) => {
        event.preventDefault()

        setShowEmojiPicker(false)

        if (messageRef.current) {
            const message = messageRef.current.value
            const start = message.slice(0, inputSelectionStart)
            const end = message.slice(inputSelectionEnd)
            messageRef.current.value = start + data.emoji + end
        }
    }

    const selectedSaveMessage = (
        savedMessageBody: string,
        savedMessageID: string,
        sendAt?: Date
    ) => {
        if (!sendAt) {
            if (messageRef.current) {
                messageRef.current.value = savedMessageBody
                onKeyUp()
                messageRef.current.focus()
            }
            setTemplateID(savedMessageID)
        }
        else {
            // Schedule this message
            const campaign = new Campaign()
            campaign.date = moment(sendAt)
            campaign.message = savedMessageBody
            campaign.template = new Template({ id: savedMessageID })
            campaign.person = currentThread as User

            RecordFactory.getInstance().sendCampaign(campaign)
        }
        setShowSavedMessagesModal(false)
    }

    let wrapperTopAdjustment = 200
    if (!templatesEnabled) wrapperTopAdjustment -= 38
    if (!widgetEnabled) wrapperTopAdjustment -= 38
    if (!contractsEnabled) wrapperTopAdjustment -= 38
    if (!formsEnabled) wrapperTopAdjustment -= 38

    return (
        <BarWrapper style={{ height: `${inputBarHeight + 38}px` }} className="inputBar shrink-0">
            <InputField>
                <textarea
                    style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        margin: '4px',
                        border: 'none',
                        width: '100%',
                        outline: 'none',
                        resize: 'none',
                        overflow: inputBarHeight >= 160 ? 'auto' : 'hidden',
                        height: `${inputBarHeight}px`,
                        maxHeight: '160px',
                    }}
                    placeholder="Send a message"
                    ref={messageRef}
                    onKeyUp={onKeyUp}
                    onKeyPress={onKeyPress}
                    onBlur={onBlur}
                />
            </InputField>
            <div className="actionButtons">
                {(templatesEnabled || widgetEnabled || contractsEnabled || formsEnabled) && (
                    <IconButton onClick={() => setShowAttachmentMenu(!showAttachmentMenu)}>
                        <MessageAttachmentIcon />
                    </IconButton>
                )}
                {showAttachmentMenu && (
                    <div
                        ref={attachmentMenuContainerRef}
                        className="attachmentWrapper"
                        style={{ top: `calc(100% - ${wrapperTopAdjustment}px)` }}
                    >
                        {templatesEnabled && (
                            <div
                                className="link"
                                onClick={() => {
                                    setShowAttachmentMenu(false)
                                    setShowSavedMessagesModal(true)
                                }}
                            >
                                Saved messages
                            </div>
                        )}
                        {widgetEnabled && (
                            <div
                                className="link"
                                onClick={() => {
                                    setShowAttachmentMenu(false)
                                    setAttachmentType('link')
                                }}
                            >
                                View & send links
                            </div>
                        )}
                        {contractsEnabled && (
                            <div
                                className="link"
                                onClick={() => {
                                    setShowAttachmentMenu(false)
                                    setAttachmentType('contract')
                                }}
                            >
                                Send a contract
                            </div>
                        )}
                        {formsEnabled && (
                            <div
                                className="link"
                                onClick={() => {
                                    setShowAttachmentMenu(false)
                                    setAttachmentType('form')
                                }}
                            >
                                Send a form
                            </div>
                        )}
                    </div>
                )}
                <IconButton onClick={selectPhoto}>
                    <MessageCameraIcon />
                </IconButton>
                <IconButton onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                    <MessageEmojiIcon />
                </IconButton>
                <input
                    type="file"
                    ref={addPhotoRef}
                    onChange={selectedPhoto}
                    accept="image/png,image/jpg,image/jpeg"
                    style={{ display: 'none' }}
                />
            </div>
            {showEmojiPicker && (
                <div ref={emojiMenuContainerRef} className="pickerWrapper">
                    <EmojiPicker onEmojiClick={onEmojiClick} />
                </div>
            )}
            {!!attachmentType && (
                <MessageAttachmentModal
                    attachmentType={attachmentType}
                    currentThread={currentThread}
                    onClose={() => setAttachmentType(undefined)}
                    onSend={handleSelectedAttachment}
                />
            )}
            {showSavedMessagesModal && (
                <TemplateModal
                    onClose={() => setShowSavedMessagesModal(false)}
                    onSend={selectedSaveMessage}
                />
            )}
        </BarWrapper>
    )
}
